import {
  $copyNode,
  $createRangeSelection,
  $isDecoratorNode,
  $isElementNode,
  $isTextNode,
  ElementNode,
  LexicalNode,
  RangeSelection,
} from 'lexical';

/**
 * Base class for the editor's inline "style wrapper" elements — FontSizeNode
 * and HighlightNode — which wrap a run of text to apply a visual style
 * (font size, highlight background). They share the same structural behavior;
 * only the stored style and rendered DOM differ.
 *
 * These extend ElementNode (not @lexical/mark's MarkNode) so their
 * `canInsertText*` methods are plain `boolean` and can be overridden freely.
 * MarkNode types them as the literal `false`, which makes "typing at the edge
 * extends the run" impossible to express without an unsafe cast.
 */
export abstract class InlineStyleElementNode extends ElementNode {
  override isInline(): true {
    return true;
  }

  // The wrapper must always contain text; Lexical garbage-collects it when
  // empty (e.g. after its only child is removed).
  override canBeEmpty(): false {
    return false;
  }

  // Typing at the leading edge extends the styled run, so text typed at the
  // start of a 24pt paragraph is also 24pt. This must stay true: when it is
  // false and a selection starts at a token node (such as a variable) at the
  // start of the run, Lexical's insertText recurses until the stack overflows.
  override canInsertTextBefore(): boolean {
    return true;
  }

  // Typing at the trailing edge extends the styled run rather than breaking
  // out into unstyled text after the wrapper. This keeps styling continuous
  // while typing (e.g. after continuing the style onto a new line).
  override canInsertTextAfter(): boolean {
    return true;
  }

  // Lexical calls this to split the wrapper in two at a point, for example
  // when Enter, a paste, or an inserted variable lands inside a styled run.
  // It moves the children after the point into the returned node, so that
  // node must be a sibling wrapper with the same style. If it is a new block,
  // the text after the point moves to a new line and loses its style.
  override insertNewAfter(
    _selection: RangeSelection,
    restoreSelection = true
  ): ElementNode {
    const newElement = $copyNode(this);
    this.insertAfter(newElement, restoreSelection);
    return newElement;
  }
}

/**
 * Move a wrapper's children out into its position and remove the now-empty
 * wrapper. Equivalent to @lexical/mark's `$unwrapMarkNode` but for any inline
 * style wrapper.
 */
export function $unwrapInlineStyleNode(node: ElementNode): void {
  const children = node.getChildren();
  let target: LexicalNode | null = null;
  for (const child of children) {
    if (target === null) {
      node.insertBefore(child);
    } else {
      target.insertAfter(child);
    }
    target = child;
  }
  node.remove();
}

/**
 * Wrap the range selection's content in freshly-created inline style wrappers,
 * grouping contiguous inline siblings under a single wrapper. A faithful,
 * generic port of @lexical/mark's `$wrapSelectionInMarkNode`:
 *
 * - `selection.extract()` first splits text nodes at the selection boundaries.
 * - Contiguous text / inline element / inline decorator nodes sharing a parent
 *   are appended into one wrapper; block or non-inline nodes reset the run.
 * - `isSameWrapper` nodes are stepped over (their children are wrapped instead)
 *   so we don't nest a wrapper directly inside the same wrapper type.
 */
export function $wrapSelectionInInlineStyleNode(
  selection: RangeSelection,
  isBackward: boolean,
  createNode: () => ElementNode,
  isSameWrapper: (node: LexicalNode) => boolean
): void {
  // Force a forwards selection since we append as we go.
  const forward = $createRangeSelection();
  const [start, end] = selection.isBackward()
    ? [selection.focus, selection.anchor]
    : [selection.anchor, selection.focus];
  forward.anchor.set(start.key, start.offset, start.type);
  forward.focus.set(end.key, end.offset, end.type);

  let currentParent: LexicalNode | null | undefined;
  let lastWrapper: ElementNode | undefined;

  const nodes = forward.extract();
  for (const node of nodes) {
    if (lastWrapper !== undefined && lastWrapper.isParentOf(node)) {
      // Already inside the wrapper we just created — nothing to do.
      continue;
    }

    let targetNode: LexicalNode | null = null;
    if ($isTextNode(node)) {
      targetNode = node;
    } else if (isSameWrapper(node)) {
      // Step into an existing same-type wrapper rather than nesting.
      continue;
    } else if (
      ($isElementNode(node) || $isDecoratorNode(node)) &&
      node.isInline()
    ) {
      targetNode = node;
    }

    if (targetNode === null) {
      // A block / non-inline node breaks the run.
      currentParent = undefined;
      lastWrapper = undefined;
      continue;
    }

    if (targetNode.is(currentParent)) continue;

    const parent = targetNode.getParent();
    if (parent == null || !parent.is(currentParent)) {
      lastWrapper = undefined;
    }
    currentParent = parent;

    if (lastWrapper === undefined) {
      lastWrapper = createNode();
      targetNode.insertBefore(lastWrapper);
    }
    lastWrapper.append(targetNode);
  }

  // Collapse the selection at the end of the wrapped run.
  if (lastWrapper !== undefined) {
    if (isBackward) {
      lastWrapper.selectStart();
    } else {
      lastWrapper.selectEnd();
    }
  }
}
